import { useAPI } from '../quipli-service/quipli-service-client';
import { useCustomerAPI } from '../customer-api/customer-api-client';
import { AxiosRequestConfig } from 'axios';
import { PlacePrediction } from '../../components/field/hooks/use-places-autocomplete';
import { useEffect, useState, useCallback, useRef } from 'react';
import { axiosInstance } from '../axios-instance';

export const useGooglePlacesAPI = (apiType: 'customer' | 'admin') => {
  const { getConfiguration: getConfigurationCustomer } = useCustomerAPI();
  const { getConfiguration: getConfigurationAdmin } = useAPI();

  const [key, setKey] = useState<string>('');
  const hasFetched = useRef(false);

  // Just fetch the key once, and then use it for all requests
  useEffect(() => {
    if (hasFetched.current) {
      return;
    }

    hasFetched.current = true;

    const fetchKey = async () => {
      const fn = apiType === 'customer' ? getConfigurationCustomer : getConfigurationAdmin;
      const googleConfiguration = await fn('CONF_MAP_GOOGLEAPI_KEY');
      setKey(googleConfiguration?.value || '');
    };

    fetchKey();
  }, [apiType]);

  // Use a callback to handle the request, so we don't recreate it on every render
  const handleGoogleRequest = useCallback(
    async <T>(req: AxiosRequestConfig): Promise<T> => {
      if (!key) {
        throw new Error('Google API key not loaded');
      }

      const requestWithApiKey = {
        ...req,
        headers: {
          ...req.headers,
          'X-Goog-Api-Key': key,
        },
      };
      const response = await axiosInstance<T>(requestWithApiKey);
      return response.data;
    },
    [key],
  );

  const placesAutocomplete = useCallback(
    async (input: string) => {
      return await handleGoogleRequest<{
        suggestions?: {
          placePrediction?: PlacePrediction;
        }[];
      }>({
        method: 'POST',
        url: 'https://places.googleapis.com/v1/places:autocomplete',
        data: { input },
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-FieldMask':
            'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat.mainText.text,suggestions.placePrediction.structuredFormat.secondaryText.text',
        },
      });
    },
    [handleGoogleRequest],
  );

  const getPlaceDetails = useCallback(
    async (placeId: string) => {
      return await handleGoogleRequest<{
        formattedAddress: string;
        addressComponents: Array<{
          longText: string;
          shortText: string;
          types: string[];
        }>;
      }>({
        method: 'GET',
        url: `https://places.googleapis.com/v1/places/${placeId}`,
        headers: {
          'X-Goog-FieldMask': 'formattedAddress,addressComponents',
        },
      });
    },
    [handleGoogleRequest],
  );

  return {
    placesAutocomplete,
    getPlaceDetails,
  };
};
